'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { ChevronLeft, Leaf, Mail, MessageCircle, Loader2, Check } from 'lucide-react';

export default function ContactPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) return;
    setIsSubmitting(true);
    // Simulate submission — in production, connect to email service
    await new Promise((r) => setTimeout(r, 1200));
    setSubmitted(true);
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen gradient-bg">
      <div className="fixed -top-20 -right-20 w-64 h-64 rounded-full opacity-20 blur-3xl pointer-events-none" style={{ background: 'radial-gradient(circle, #A8C5AD, transparent)' }} />

      <div className="max-w-lg mx-auto px-5 py-8 pb-16">
        <div className="flex items-center gap-3 mb-8">
          <Link
            href="/profile"
            className="w-9 h-9 rounded-xl bg-white/80 border border-border flex items-center justify-center shadow-card-sm hover:bg-white transition-colors"
          >
            <ChevronLeft size={18} className="text-foreground" />
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl lavender-gradient flex items-center justify-center shadow-card-sm">
              <Leaf size={14} className="text-white" />
            </div>
            <h1 className="text-lg font-extrabold text-foreground tracking-tight">Contact Us</h1>
          </div>
        </div>

        {/* Quick links */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <a
            href="mailto:hello@mylittleclingy.com"
            className="flex items-center gap-2.5 p-3.5 rounded-2xl bg-white/80 border border-border shadow-card-sm hover:bg-white transition-colors"
          >
            <div className="w-8 h-8 rounded-xl lavender-gradient flex items-center justify-center flex-shrink-0">
              <Mail size={14} className="text-white" />
            </div>
            <div>
              <p className="text-xs font-bold text-foreground">Email Us</p>
              <p className="text-[10px] text-muted-foreground">hello@mylittleclingy.com</p>
            </div>
          </a>
          <Link
            href="/safety"
            className="flex items-center gap-2.5 p-3.5 rounded-2xl bg-white/80 border border-border shadow-card-sm hover:bg-white transition-colors"
          >
            <div className="w-8 h-8 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
              <MessageCircle size={14} className="text-red-500" />
            </div>
            <div>
              <p className="text-xs font-bold text-foreground">Crisis Help</p>
              <p className="text-[10px] text-muted-foreground">Safety resources</p>
            </div>
          </Link>
        </div>

        {submitted ? (
          <div className="bg-white/80 rounded-2xl border border-border shadow-card-sm p-8 flex flex-col items-center text-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
              <Check size={28} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-extrabold text-foreground mb-1">Message sent 💜</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Thanks for reaching out. We'll get back to you within 1–2 business days.
              </p>
            </div>
            <Link href="/profile" className="text-sm font-semibold text-primary hover:underline">
              ← Back to Profile
            </Link>
          </div>
        ) : (
          <div className="bg-white/80 rounded-2xl border border-border shadow-card-sm p-6">
            <h2 className="text-base font-extrabold text-foreground mb-1">Send us a message</h2>
            <p className="text-sm text-muted-foreground mb-5">We read every message and aim to reply within 1–2 business days.</p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-foreground">Your Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  required
                  className="w-full px-4 py-3.5 rounded-2xl border border-border bg-input text-foreground placeholder:text-muted-foreground text-sm outline-none transition-all duration-200 focus:shadow-input focus:border-primary"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-foreground">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="w-full px-4 py-3.5 rounded-2xl border border-border bg-input text-foreground placeholder:text-muted-foreground text-sm outline-none transition-all duration-200 focus:shadow-input focus:border-primary"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-foreground">Subject</label>
                <select
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full px-4 py-3.5 rounded-2xl border border-border bg-input text-foreground text-sm outline-none transition-all duration-200 focus:shadow-input focus:border-primary"
                >
                  <option value="">Select a topic…</option>
                  <option value="general">General question</option>
                  <option value="account">Account issue</option>
                  <option value="privacy">Privacy / data request</option>
                  <option value="safety">Safety concern</option>
                  <option value="feedback">Feedback or suggestion</option>
                  <option value="bug">Bug report</option>
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-foreground">Message</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Tell us how we can help…"
                  required
                  rows={5}
                  className="w-full px-4 py-3.5 rounded-2xl border border-border bg-input text-foreground placeholder:text-muted-foreground text-sm outline-none transition-all duration-200 focus:shadow-input focus:border-primary resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting || !name.trim() || !email.trim() || !message.trim()}
                className="w-full py-4 rounded-2xl lavender-gradient text-white font-semibold text-base shadow-card transition-all duration-200 active:scale-95 disabled:opacity-70 flex items-center justify-center gap-2 mt-1"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    <span>Sending…</span>
                  </>
                ) : (
                  'Send Message'
                )}
              </button>
            </form>
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <Link href="/privacy-policy" className="text-xs text-primary font-semibold hover:underline">Privacy Policy</Link>
          <span className="text-xs text-muted-foreground">·</span>
          <Link href="/terms" className="text-xs text-primary font-semibold hover:underline">Terms</Link>
          <span className="text-xs text-muted-foreground">·</span>
          <Link href="/safety" className="text-xs text-primary font-semibold hover:underline">Safety</Link>
        </div>
      </div>
    </div>
  );
}
